'use client';

import { OverlayDeck } from '@/components/patterns/OverlayDeck';
import { MagnifierIcon } from '@/components/primitives/icons/MagnifierIcon';
import { Media } from '@/components/primitives/Media';
import { GA_OVERLAY_SET } from '@/constants/analytics';
import {
  ENABLER_CIRCLE_DISPLAY_SIZE,
  ENABLER_CIRCLE_PHOTO_SIZE,
} from '@/constants/enablerCircles';
import { getEnablerCircleTexture } from '@/content/data/enabler-circle-textures';
import { modernMediaFormats } from '@/lib/mediaFormats';

import styles from './EnablingCircles.module.css';

import type { OverlayLabels } from '@/components/patterns/Overlay';
import type { ResolvedOverlayEntry } from '@/content/data/overlay-entries';
import type { CSSProperties } from 'react';

type EnablingCirclesProps = {
  entries: readonly ResolvedOverlayEntry[];
  labels: OverlayLabels;
};

type CircleMaskStyle = CSSProperties & {
  '--circle-mask'?: string;
};

export function EnablingCircles({ entries, labels }: EnablingCirclesProps) {
  return (
    <OverlayDeck
      entries={entries}
      labels={labels}
      overlaySet={GA_OVERLAY_SET.enablers}
      renderTriggers={(triggers) => (
        <div className={styles.scroller}>
          <div className={styles.row}>{triggers.slice(0, 3)}</div>
          <div className={styles.rowEnd}>{triggers.slice(3)}</div>
        </div>
      )}
      renderTrigger={(entry, { triggerProps, isOpen, isActive }) => {
        const texture = getEnablerCircleTexture(entry.id);
        const circleStyle: CircleMaskStyle | undefined = texture
          ? { '--circle-mask': `url('${texture.maskSrc}')` }
          : undefined;
        const photoSources = texture
          ? ([{ src: texture.photoSrc, width: ENABLER_CIRCLE_PHOTO_SIZE }] as const)
          : null;

        return (
          <button
            key={entry.id}
            type="button"
            className={[styles.circle, isOpen && isActive ? styles.circleActive : '']
              .filter(Boolean)
              .join(' ')}
            style={circleStyle}
            {...triggerProps}
          >
            {photoSources ? (
              <span className={styles.circleTexture} aria-hidden>
                <Media
                  sources={photoSources}
                  formats={modernMediaFormats(photoSources)}
                  sizes={`${ENABLER_CIRCLE_DISPLAY_SIZE}px`}
                  alt=""
                  width={ENABLER_CIRCLE_PHOTO_SIZE}
                  height={ENABLER_CIRCLE_PHOTO_SIZE}
                />
              </span>
            ) : null}
            <span className={styles.circleScrim} aria-hidden />
            <span className={styles.circleContent}>
              <span className={styles.circleLabel}>{entry.title}</span>
              <span className={styles.circleMagnifier} aria-hidden>
                <MagnifierIcon />
              </span>
            </span>
          </button>
        );
      }}
    />
  );
}
